import { Box, Divider, VStack } from '@chakra-ui/react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import { NavItem, SecondaryNavItem, SidebarShell } from '../sidebarChrome';
import { buildNavIa, type NavIaChild, type NavIaItem } from './tree';

// Replaces the unprefixed lists when VITE_NAV_IA_V2_ENABLED === 'true'.
// Chrome (width, brand mark, account row) is the existing sidebar shell.
// AgentPanel collapse is a separate open item and is not drawn here.

export function NavIaV2Sidebar() {
  const auth = useAuth();
  const tree = buildNavIa(auth);
  const logoTo = tree.primary.some((item) => item.id === 'home') ? '/v2/home' : '/home';

  return (
    <SidebarShell logoTo={logoTo} testId="nav-ia-v2">
      <VStack
        as="nav"
        aria-label="Primary"
        align="stretch"
        spacing={1}
        mt={6}
        data-testid="nav-ia-v2-primary"
      >
        {tree.primary.map((item, index) => (
          <NavGroup key={item.id} item={item} index={index + 1} />
        ))}
      </VStack>

      <Box mt="auto">
        <Divider my={4} />
        <VStack
          as="nav"
          aria-label="Secondary"
          align="stretch"
          spacing={0.5}
          data-testid="nav-ia-v2-secondary"
        >
          {tree.secondary.map((item) => (
            <NavGroup key={item.id} item={item} />
          ))}
        </VStack>
      </Box>
    </SidebarShell>
  );
}

function NavGroup({ item, index }: { item: NavIaItem; index?: number }) {
  return (
    <Box>
      <NavLink to={item.to} style={{ textDecoration: 'none' }} data-testid={item.testId}>
        {({ isActive }) => (
          index !== undefined
            ? (
              <NavItem
                index={index}
                label={item.label}
                description={item.description}
                isActive={isActive}
              />
            )
            : <SecondaryNavItem label={item.label} isActive={isActive} />
        )}
      </NavLink>
      {item.children.map((child) => (
        <NavChildLink key={child.id} child={child} />
      ))}
    </Box>
  );
}

function NavChildLink({ child }: { child: NavIaChild }) {
  const location = useLocation();
  const isActive = childIsActive(child.to, location.pathname, location.search);
  return (
    <NavLink to={child.to} style={{ textDecoration: 'none' }} data-testid={child.testId}>
      <Box pl={6}>
        <SecondaryNavItem label={child.label} isActive={isActive} />
      </Box>
    </NavLink>
  );
}

function childIsActive(to: string, pathname: string, search: string): boolean {
  const url = new URL(to, 'https://reach-social.invalid');
  if (url.pathname !== pathname) return false;
  if (!url.search) return true;
  const wanted = new URLSearchParams(url.search);
  const have = new URLSearchParams(search);
  for (const [key, value] of wanted) {
    if (have.get(key) !== value) return false;
  }
  return true;
}
